import * as React from 'react';
import { YeniRezervasyon, type YeniRezervasyonHazir } from '@/features/reservations/YeniRezervasyon';
import { RezervasyonDuzelt } from './RezervasyonDuzelt';
import { RezervasyonIptal } from './RezervasyonIptal';
import { ApiHatasi, api, oturum } from '@/lib/api';
import { paraFormatla } from '@/lib/utils';

/**
 * Oda planı ekranı.
 *
 * Tasarım kaynağı: Claude Design — "otelcms Oda Plani.dc.html".
 * Tasarımdaki rezervasyonlar üreticiydi; buradaki her şey gerçek uçlardan gelir:
 *   GET   /api/rezervasyonlar/oda-plani   -> odalar + rezervasyonlar + KBS durumu
 *   PATCH /api/rezervasyonlar/:id         -> sürükle-bırak taşıma
 *   POST  /api/checkin/:id/cikis          -> check-out
 *
 * Çakışma kontrolü iki kattır: anında geri bildirim için istemcide, asıl karar
 * sunucuda (Durable Object kilidi + veritabanı kontrolü). Sunucu reddederse
 * kutu eski yerine döner ve Türkçe hata gösterilir.
 */

/**
 * Izgara olculeri.
 *
 * Telefonda 14 gun x 58px = 812px yatay yol demek; ekranda ancak 4-5 gun
 * goruluyor ve gorevli iki eksende birden kayboluyordu. Dar ekranda pencere
 * yariya iner ve satirlar kisalir - ayni ekranda iki kat is gorunur.
 */
const GUN_SAYISI = 14;
const GUN_SAYISI_DAR = 7;
const SATIR_YUKSEKLIGI = 76;
const SATIR_YUKSEKLIGI_DAR = 60;
const ETIKET_GENISLIGI = 200;
/** Dar ekranda oda sutunu daralir; yoksa izgaraya yer kalmiyor. */
const ETIKET_GENISLIGI_DAR = 84;
/**
 * Bir gun sutununun en az genisligi.
 *
 * Onceden sutunlar `1fr` idi: 390px'de 14 gun ~13px'e sikisiyor ve tarih
 * basliklari ust uste binip okunamaz hale geliyordu. Izgara EZILMEK yerine
 * KAYMALI - bu sayi o esigi koyar.
 */
const MIN_GUN_GENISLIGI = 58;
/*
 * Telefonda gun sutunu daralir. Amac tek: 100 + 7x40 = 380px, yani 390px'lik
 * bir ekranda TUM HAFTA yatay kaydirma olmadan gorunur. Tape chart'ta asil
 * yuk iki eksende birden kaybolmaktir; yatay ekseni tumden kaldirmak
 * telefondaki en buyuk kazanc.
 */
const MIN_GUN_GENISLIGI_DAR = 40;

interface Oda {
  id: string;
  no: string;
  kat: string | null;
  durum: 'BOS' | 'DOLU' | 'ARIZALI' | 'BLOKE';
  temizlikDurumu: 'TEMIZ' | 'KIRLI' | 'TEMIZLENIYOR' | 'KONTROL_BEKLIYOR';
  roomTypeId: string;
  roomTypeAd: string | null;
}
interface Rez {
  id: string;
  kod: string;
  roomId: string | null;
  girisTarihi: string;
  cikisTarihi: string;
  durum: 'OPSIYON' | 'ONAYLI' | 'CHECKED_IN' | 'CHECKED_OUT' | 'IPTAL' | 'NO_SHOW';
  kaynak: string;
  yetiskin: number;
  cocuk: number;
  toplamKurus: number;
  paraBirimi: string;
  misafirAd: string | null;
  misafirSoyad: string | null;
  not: string | null;
  kaporaKurus: number;
  faturaKurumsalMi: boolean;
  faturaUnvan: string | null;
  kbsDurum: string | null;
}
/** KBS aktaricisinin durumu; `gerekli` degilse tesis resmi WS kullanmiyor. */
interface Aktarici {
  gerekli: boolean;
  calisiyorMu: boolean;
  sessizlikDk: number | null;
}

const DURUM_RENK: Record<string, [string, string, string]> = {
  CHECKED_IN: ['#1f4fd8', '#fff', 'Konaklıyor'],
  ONAYLI: ['#12a150', '#fff', 'Onaylı'],
  OPSIYON: ['#f2b632', '#0b1530', 'Beklemede'],
};
const KBS_METIN: Record<string, [string, string]> = {
  GONDERILDI: ['Gönderildi', '#5fd48f'],
  BEKLIYOR: ['Sırada', '#f2b632'],
  GONDERILIYOR: ['Gönderiliyor', '#8fb0ff'],
  SURESI_DOLMAK_UZERE: ['Süresi doluyor', '#ff8b8b'],
  HATA: ['Hata', '#ff8b8b'],
};
const KAYNAK_ADI: Record<string, string> = {
  DIREKT: 'Direkt', TELEFON: 'Telefon', WALK_IN: 'Walk-in', ACENTA: 'Acenta',
  WEB: 'Web', KANAL: 'Kanal', GRUP: 'Grup',
};

/**
 * Misafirin gorunen adi.
 *
 * Panelde yalnizca soyad yaziyordu; ayni soyadli iki misafir ya da tek
 * kelimelik kayitlar ayirt edilemiyordu. Ad ve soyad birlikte yazilir,
 * ikisi de yoksa rezervasyon kodu gosterilir.
 */
function tamAd(rez: { misafirAd: string | null; misafirSoyad: string | null; kod: string }): string {
  const ad = [rez.misafirAd, rez.misafirSoyad].map((p) => p?.trim()).filter(Boolean).join(' ');
  return ad || rez.kod;
}

const isoGun = (d: Date) => d.toISOString().slice(0, 10);
const gunEkle = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoGun(d);
};
const gunFarki = (a: string, b: string) =>
  Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86_400_000);
const kisa = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
const uzun = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'short' });
const haftaSonu = (iso: string) => [0, 6].includes(new Date(`${iso}T00:00:00`).getDay());

/**
 * Dar ekran mi? Izgara olculeri buna gore degisir.
 * Tailwind siniflari yetmiyor cunku genislikler satir ici stilde (grid
 * sablonu) kullaniliyor.
 */
function darEkranMi(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
}

function useDarEkran(): boolean {
  const [dar, setDar] = React.useState(darEkranMi);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const dinle = () => setDar(mq.matches);
    mq.addEventListener('change', dinle);
    return () => mq.removeEventListener('change', dinle);
  }, []);
  return dar;
}

export function OdaPlani({
  tenantId,
  yetkiler,
  onCheckIn,
}: {
  tenantId: string;
  yetkiler: string[];
  onCheckIn: (rezId: string) => void;
}) {
  const yazabilir = yetkiler.includes('reservation:write');
  const bugun = React.useMemo(() => isoGun(new Date()), []);
  const [baslangic, setBaslangic] = React.useState(bugun);
  const [odalar, setOdalar] = React.useState<Oda[]>([]);
  const [rezervasyonlar, setRezervasyonlar] = React.useState<Rez[]>([]);
  const [secili, setSecili] = React.useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const dar = useDarEkran();
  const etiketGen = dar ? ETIKET_GENISLIGI_DAR : ETIKET_GENISLIGI;
  const gunSayisi = dar ? GUN_SAYISI_DAR : GUN_SAYISI;
  const satirY = dar ? SATIR_YUKSEKLIGI_DAR : SATIR_YUKSEKLIGI;
  /** Izgaranin en az genisligi; bunun altinda kaydirma cubugu devreye girer. */
  const izgaraEnAz = etiketGen + gunSayisi * (dar ? MIN_GUN_GENISLIGI_DAR : MIN_GUN_GENISLIGI);
  const [bildirim, setBildirim] = React.useState<{ metin: string; iyi: boolean } | null>(null);

  const izgaraRef = React.useRef<HTMLDivElement>(null);
  /** Izgaranin kendi kaydirma kutusu (surukleme sirasinda kenarda otomatik kayar). */
  const kaydirmaRef = React.useRef<HTMLDivElement>(null);
  /**
   * Dokunmada uzun basma sayaci.
   *
   * Telefonda bara dokunur dokunmaz surukleme baslarsa, plani kaydirmak
   * isteyen her parmak kaydirmayi rezervasyon tasimaya cevirir. Uzun basma
   * (350ms) niyeti ayirir: kisa dokunus = ac, basili tut = tasi.
   */
  const basmaRef = React.useRef<number | undefined>(undefined);
  const [kavradi, setKavradi] = React.useState<string | null>(null);
  /** Kenarda otomatik kaydirma dongusu. */
  const kaydirmaDonguRef = React.useRef<number | undefined>(undefined);
  const imlecRef = React.useRef({ x: 0, y: 0 });

  /**
   * Surukleme kenara yaklasinca izgarayi kendiliginden kaydir.
   *
   * Bu olmadan gorunen alanin disindaki bir gune tasimak IMKANSIZ: parmak
   * ekranin kenarina dayaniyor, izgara yerinde duruyordu. Telefonda ayni anda
   * yalnizca 4-5 gun gorundugu icin bu, tasimayi kullanilmaz kiliyordu.
   */
  const kenarKaydirmasiniBaslat = React.useCallback(() => {
    if (kaydirmaDonguRef.current) return;
    const adim = () => {
      const kutu = kaydirmaRef.current;
      if (!kutu || !surukleRef.current) {
        kaydirmaDonguRef.current = undefined;
        return;
      }
      const k = kutu.getBoundingClientRect();
      const esik = 56;
      const hiz = 14;
      const { x, y } = imlecRef.current;
      if (x - k.left < esik) kutu.scrollLeft -= hiz;
      else if (k.right - x < esik) kutu.scrollLeft += hiz;
      if (y - k.top < esik) kutu.scrollTop -= hiz;
      else if (k.bottom - y < esik) kutu.scrollTop += hiz;
      kaydirmaDonguRef.current = requestAnimationFrame(adim);
    };
    kaydirmaDonguRef.current = requestAnimationFrame(adim);
  }, []);

  const kenarKaydirmasiniDurdur = React.useCallback(() => {
    if (kaydirmaDonguRef.current) cancelAnimationFrame(kaydirmaDonguRef.current);
    kaydirmaDonguRef.current = undefined;
  }, []);

  React.useEffect(() => () => {
    kenarKaydirmasiniDurdur();
    if (basmaRef.current) window.clearTimeout(basmaRef.current);
  }, [kenarKaydirmasiniDurdur]);
  /*
   * Surukleme durumu.
   *
   * `gunGen` ve `odaIndex` BASLANGICTA olculur: hedef her karede yeniden
   * hesaplanacagi icin her seferinde DOM olcmek gereksiz is olurdu.
   */
  const surukleRef = React.useRef<{
    id: string;
    x0: number;
    y0: number;
    gunGen: number;
    odaIndex: number;
  } | null>(null);
  const [suruklenen, setSuruklenen] = React.useState<{ id: string; dx: number; dy: number } | null>(null);
  const hareketEttiRef = React.useRef(false);
  /** Reddedilen tasima: kisa bir sarsinti ile geri doner. */
  const [reddedilen, setReddedilen] = React.useState<string | null>(null);
  /**
   * Onay bekleyen tasima.
   *
   * Surukleme kolay bir jesttir - fazlasiyla kolay: masaustunde yanlislikla
   * kavranan bir bar, telefonda parmagin kaymasi rezervasyonu sessizce baska
   * odaya tasiyordu ve gorevli cogu zaman fark etmiyordu. Sonucu oda planinin
   * sessizce yanlislasmasi. Artik degisiklik EKRANDA uygulanir (gorevli sonucu
   * gorur) ama sunucuya ancak onaydan sonra gider; vazgecilirse geri alinir.
   */
  const [onayBekleyen, setOnayBekleyen] = React.useState<{
    rez: Rez;
    eskiOdaNo: string;
    yeniOda: Oda;
    yeniGiris: string;
    yeniCikis: string;
    oncekiler: Rez[];
  } | null>(null);

  /**
   * KBS aktaricisinin yasam belirtisi.
   *
   * Sunucu soyluyor, biz oldugu gibi gosteriyoruz. Onemli olan sudur: bu
   * bilgiyi gizlemek yerine gostermek, resepsiyonun gerekirse KBS'ye elle
   * girmesini saglar. Yasal bildirim suresi 24 saattir.
   */
  const [aktarici, setAktarici] = React.useState<Aktarici | null>(null);

  const gunler = React.useMemo(
    () => Array.from({ length: gunSayisi }, (_, i) => gunEkle(baslangic, i)),
    [baslangic, gunSayisi],
  );
  const bitis = gunEkle(baslangic, gunSayisi);

  const yukle = React.useCallback(async () => {
    try {
      const veri = await api<{ odalar: Oda[]; rezervasyonlar: Rez[]; aktarici?: Aktarici }>(
        `/api/rezervasyonlar/oda-plani?baslangic=${baslangic}&bitis=${bitis}`,
      );
      setOdalar(veri.odalar);
      setRezervasyonlar(veri.rezervasyonlar);
      setAktarici(veri.aktarici ?? null);
    } catch (e) {
      duyur(e instanceof ApiHatasi ? e.message : 'Oda planı alınamadı.', false);
    } finally {
      setYukleniyor(false);
    }
  }, [baslangic, bitis]);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  // Canlı yayın: başka bir resepsiyonist değişiklik yapınca ekran tazelenir
  React.useEffect(() => {
    const token = oturum.token();
    if (!token) return;
    const ws = new WebSocket(
      `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/${tenantId}?token=${token}`,
    );
    ws.onmessage = (olay) => {
      const m = JSON.parse(olay.data as string) as { tip: string };
      if (m.tip === 'REZERVASYON_GUNCELLENDI' || m.tip === 'ODA_GUNCELLENDI') void yukle();
    };
    const ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [tenantId, yukle]);

  const zamanlayiciRef = React.useRef<number | undefined>(undefined);
  function duyur(metin: string, iyi: boolean) {
    setBildirim({ metin, iyi });
    if (zamanlayiciRef.current) window.clearTimeout(zamanlayiciRef.current);
    zamanlayiciRef.current = window.setTimeout(() => setBildirim(null), 2600);
  }

  /** İstemci tarafı çakışma kontrolü — anında geri bildirim için. */
  function cakisiyorMu(hedef: { id: string; roomId: string; giris: string; cikis: string }) {
    return rezervasyonlar.some(
      (r) =>
        r.id !== hedef.id &&
        r.roomId === hedef.roomId &&
        ['OPSIYON', 'ONAYLI', 'CHECKED_IN'].includes(r.durum) &&
        r.girisTarihi < hedef.cikis &&
        hedef.giris < r.cikisTarihi,
    );
  }

  /**
   * Suruklenen rezervasyonun SU AN nereye inecegi.
   *
   * Onceden bar imleci birebir takip ediyor, hedef ise ancak birakilinca
   * yuvarlaniyordu: gorevlinin gordugu yer ile rezervasyonun indigi yer ayni
   * degildi. Artik hedef her karede hesaplanir; bar da bu hedefe yapisir.
   */
  const hedef = React.useMemo(() => {
    const s = surukleRef.current;
    if (!suruklenen || !s) return null;
    const rez = rezervasyonlar.find((r) => r.id === suruklenen.id);
    if (!rez) return null;

    const satirKay = Math.round(suruklenen.dy / satirY);
    const gunKay = Math.round(suruklenen.dx / s.gunGen);
    const odaIndex = Math.max(0, Math.min(odalar.length - 1, s.odaIndex + satirKay));
    const oda = odalar[odaIndex];
    if (!oda) return null;

    const giris = gunEkle(rez.girisTarihi, gunKay);
    const cikis = gunEkle(rez.cikisTarihi, gunKay);
    const cakisma = cakisiyorMu({ id: rez.id, roomId: oda.id, giris, cikis });
    const degisti = oda.id !== rez.roomId || gunKay !== 0;

    return {
      rez,
      oda,
      odaIndex,
      gunKay,
      giris,
      cikis,
      cakisma,
      degisti,
      // Barin YAPISACAGI kayma (piksel) - imlecin ham kaymasi degil
      snapDx: gunKay * s.gunGen,
      snapDy: (odaIndex - s.odaIndex) * satirY,
    };
  }, [suruklenen, odalar, rezervasyonlar]);

  /**
   * Surukleme sirasinda odalarin gunluk dolulugu.
   *
   * "Gecerli seritler" bundan cizilir: gorevli bos araligi aramak yerine
   * dogrudan uygun yerlere kilitlenir (bkz. Lazyweb raporu, Hipotez #1).
   */
  const dolulukHaritasi = React.useMemo(() => {
    if (!suruklenen) return null;
    const harita = new Map<string, boolean[]>();
    for (const oda of odalar) {
      harita.set(
        oda.id,
        gunler.map((g) =>
          rezervasyonlar.some(
            (r) =>
              r.id !== suruklenen.id &&
              r.roomId === oda.id &&
              ['OPSIYON', 'ONAYLI', 'CHECKED_IN'].includes(r.durum) &&
              r.girisTarihi <= g &&
              r.cikisTarihi > g,
          ),
        ),
      );
    }
    return harita;
  }, [suruklenen, odalar, rezervasyonlar, gunler]);

  /** Rezervasyonun sigabilecegi ardisik bos araliklar (baslangic, uzunluk). */
  const seritler = React.useCallback(
    (odaId: string, gece: number): { bas: number; uzunluk: number }[] => {
      const dolu = dolulukHaritasi?.get(odaId);
      if (!dolu) return [];
      const cikti: { bas: number; uzunluk: number }[] = [];
      let bas = -1;
      for (let i = 0; i <= dolu.length; i++) {
        if (i < dolu.length && !dolu[i]) {
          if (bas < 0) bas = i;
        } else if (bas >= 0) {
          if (i - bas >= gece) cikti.push({ bas, uzunluk: i - bas });
          bas = -1;
        }
      }
      return cikti;
    },
    [dolulukHaritasi],
  );

  /** Tasima reddedildi: kisa bir sarsinti goster. */
  function reddet(id: string, mesaj: string) {
    duyur(mesaj, false);
    setReddedilen(id);
    window.setTimeout(() => setReddedilen((o) => (o === id ? null : o)), 500);
  }

  async function birak() {
    if (basmaRef.current) {
      window.clearTimeout(basmaRef.current);
      basmaRef.current = undefined;
    }
    setKavradi(null);
    kenarKaydirmasiniDurdur();
    const s = surukleRef.current;
    const gecici = suruklenen;
    surukleRef.current = null;
    setSuruklenen(null);
    if (!s || !gecici || !hareketEttiRef.current) {
      setTimeout(() => (hareketEttiRef.current = false), 0);
      return;
    }
    setTimeout(() => (hareketEttiRef.current = false), 0);

    const rez = rezervasyonlar.find((r) => r.id === s.id);
    if (!rez || !rez.roomId) return;

    /*
     * Hedef, surukleme boyunca ekranda gosterilen hedefin AYNISIDIR.
     * Onceden burada yeniden hesaplaniyordu ve dar ekranda etiket sutunu
     * 128px iken sabit 200px varsayildigi icin gun kaymasi yanlis cikiyordu.
     */
    const yeniIndex = Math.max(
      0,
      Math.min(odalar.length - 1, s.odaIndex + Math.round(gecici.dy / satirY)),
    );
    const kaydirma = Math.round(gecici.dx / s.gunGen);
    const yeniOda = odalar[yeniIndex]!;
    const yeniGiris = gunEkle(rez.girisTarihi, kaydirma);
    const yeniCikis = gunEkle(rez.cikisTarihi, kaydirma);

    if (yeniOda.id === rez.roomId && kaydirma === 0) return;

    if (cakisiyorMu({ id: rez.id, roomId: yeniOda.id, giris: yeniGiris, cikis: yeniCikis })) {
      reddet(rez.id, 'Oda bu tarihlerde dolu — taşınmadı');
      return;
    }

    /*
     * Degisiklik ONCE ekranda uygulanir - gorevli neyi onayladigini gorsun -
     * ama sunucuya ancak onaydan sonra gider.
     */
    const eskiOdaNo = odalar.find((o) => o.id === rez.roomId)?.no ?? '—';
    const oncekiler = rezervasyonlar;
    setRezervasyonlar((o) =>
      o.map((r) => (r.id === rez.id ? { ...r, roomId: yeniOda.id, girisTarihi: yeniGiris, cikisTarihi: yeniCikis } : r)),
    );
    setSecili(null);
    setOnayBekleyen({ rez, eskiOdaNo, yeniOda, yeniGiris, yeniCikis, oncekiler });
  }

  /** Onay penceresinden "Taşı": degisiklik sunucuya gider. */
  async function tasimayiOnayla() {
    const b = onayBekleyen;
    if (!b) return;
    setOnayBekleyen(null);
    try {
      await api(`/api/rezervasyonlar/${b.rez.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ roomId: b.yeniOda.id, girisTarihi: b.yeniGiris, cikisTarihi: b.yeniCikis }),
      });
      duyur(`${tamAd(b.rez)} → Oda ${b.yeniOda.no}, ${kisa(b.yeniGiris)}`, true);
    } catch (e) {
      setRezervasyonlar(b.oncekiler);
      reddet(b.rez.id, e instanceof ApiHatasi ? e.message : 'Taşınamadı.');
    }
  }

  /** Onay penceresinden "Vazgec": ekrandaki degisiklik geri alinir. */
  function tasimadanVazgec() {
    const b = onayBekleyen;
    if (!b) return;
    setOnayBekleyen(null);
    setRezervasyonlar(b.oncekiler);
    setReddedilen(b.rez.id);
    window.setTimeout(() => setReddedilen((o) => (o === b.rez.id ? null : o)), 500);
  }

  const [yeniRez, setYeniRez] = React.useState<YeniRezervasyonHazir | null>(null);
  const [duzelt, setDuzelt] = React.useState<'uzat' | 'duzelt' | null>(null);
  const [iptalAcik, setIptalAcik] = React.useState(false);

  /** Bos hucreye dokunma: o oda + o gun icin bir gecelik hazir form. */
  function bosHucre(odaId: string, gun: string) {
    if (!yazabilir || hareketEttiRef.current) return;
    setYeniRez({ roomId: odaId, girisTarihi: gun, cikisTarihi: gunEkle(gun, 1) });
  }

  const seciliRez = rezervasyonlar.find((r) => r.id === secili) ?? null;
  const seciliOda = seciliRez ? odalar.find((o) => o.id === seciliRez.roomId) : null;

  async function anaIslem() {
    if (!seciliRez) return;
    if (seciliRez.durum === 'CHECKED_IN') {
      try {
        await api(`/api/checkin/${seciliRez.id}/cikis`, { method: 'POST' });
        duyur('Check-out tamamlandı · KBS çıkış bildirimi sıraya girdi', true);
        setSecili(null);
        await yukle();
      } catch (e) {
        duyur(e instanceof ApiHatasi ? e.message : 'Check-out yapılamadı.', false);
      }
    } else {
      onCheckIn(seciliRez.id);
    }
  }

  const icerdekiler = rezervasyonlar.filter(
    (r) => r.durum === 'CHECKED_IN' || (r.girisTarihi <= bugun && r.cikisTarihi > bugun && r.durum === 'ONAYLI'),
  ).length;
  const bugunGiris = rezervasyonlar.filter((r) => r.girisTarihi === bugun && r.durum !== 'CHECKED_IN').length;
  const kbsSirada = rezervasyonlar.filter((r) => r.kbsDurum === 'BEKLIYOR' || r.kbsDurum === 'GONDERILIYOR').length;
  const kirliOda = odalar.filter((o) => o.temizlikDurumu === 'KIRLI').length;

  /**
   * "otomatik gidecek" yalnizca GERCEKTEN gidecekse yazilir.
   *
   * Aktarici susmussa bildirim gitmiyor; eski hali panelde her sey yolundaymis
   * gibi gorunuyordu. Bir bildirimin kacmasi idari para cezasi, tekrari
   * ruhsat iptaline kadar gidebiliyor - ekranin dogru soylemesi sart.
   */
  const kbsAltMetin: [string, string] =
    aktarici?.gerekli && !aktarici.calisiyorMu
      ? ['aktarıcı yanıt vermiyor', '#c0392b']
      : ['otomatik gidecek', '#5b6480'];

  return (
    // Telefonda dis bosluk kisilir: her piksel gun sutununa gider.
    <main className="flex flex-1 flex-col gap-4 bg-zemin p-3 sm:gap-[22px] sm:p-7">
      {/* Başlık */}
      <div className="flex flex-wrap items-center gap-2.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both] sm:gap-3.5">
        <h1 className="mr-2 w-full text-2xl font-bold tracking-[-.03em] sm:w-auto sm:text-3xl">Oda planı</h1>
        <div className="inline-flex flex-1 items-center overflow-hidden rounded-xl border border-cizgi bg-white sm:flex-none">
          <button onClick={() => setBaslangic(gunEkle(baslangic, -7))} className="px-3 py-2.5 text-gri hover:bg-zemin hover:text-lacivert" aria-label="Önceki hafta">‹</button>
          <span className="flex-1 px-1.5 text-center text-[14px] font-semibold sm:min-w-[210px] sm:text-[15px]">
            {kisa(baslangic)} – {kisa(gunler[gunSayisi - 1]!)}
          </span>
          <button onClick={() => setBaslangic(gunEkle(baslangic, 7))} className="px-3 py-2.5 text-gri hover:bg-zemin hover:text-lacivert" aria-label="Sonraki hafta">›</button>
        </div>
        <button onClick={() => setBaslangic(bugun)} className="rounded-xl border border-cizgi bg-white px-4 py-2.5 text-sm font-semibold text-lacivert hover:border-lacivert">
          Bugün
        </button>
        {yazabilir && (
          <button
            onClick={() => setYeniRez({})}
            className="flex items-center gap-2 rounded-xl bg-marka-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_-14px_rgba(31,79,216,.8)] transition hover:bg-marka-700"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {/* Dar ekranda "Yeni rezervasyon" satiri tek basina dolduruyordu. */}
            <span className="sm:hidden">Yeni</span>
            <span className="hidden sm:inline">Yeni rezervasyon</span>
          </button>
        )}
        {/*
          Renk aciklamasi masaustu ayrintisi: telefonda bir satiri tumden
          yiyip asil icerigi (izgarayi) katlamanin altina itiyordu.
          Renkler zaten izgarada goruluyor.
        */}
        <div className="ml-auto hidden flex-wrap gap-4 text-[13px] font-medium text-gri md:flex">
          {(['CHECKED_IN', 'ONAYLI', 'OPSIYON'] as const).map((d) => (
            <span key={d} className="flex items-center gap-[7px]">
              <span className="size-3 rounded" style={{ background: DURUM_RENK[d]![0] }} />
              {DURUM_RENK[d]![2]}
            </span>
          ))}
          <span className="flex items-center gap-[7px]">
            <span className="size-3 rounded border-[1.5px] border-dashed border-[#b9840f] bg-[#fff5dc]" />
            Kirli oda
          </span>
        </div>
      </div>

      {/* KPI */}
      {/*
        Telefonda dort kart alt alta ~400px yer yiyor ve asil is olan izgara
        katlanin cok altina duşuyordu. Dar ekranda 2x2 ve kompakt.
      */}
      <div className="grid grid-cols-2 gap-2.5 [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:gap-3 xl:grid-cols-4">
        {([
          ['Doluluk', odalar.length ? `%${Math.round((icerdekiler / odalar.length) * 100)}` : '—', `${icerdekiler} / ${odalar.length} oda`, '#1f4fd8'],
          ['Bugün giriş', String(bugunGiris), 'kimlik bekliyor', '#b9840f'],
          ['KBS sırada', String(kbsSirada), ...kbsAltMetin],
          ['Kirli oda', String(kirliOda), 'kat hizmetlerinde', '#b9840f'],
        ] as [string, string, string, string][]).map(([etiket, deger, alt, renk]) => (
          <div key={etiket} className="flex flex-col gap-1 rounded-2xl border border-cizgi bg-white p-3 sm:gap-1.5 sm:p-4">
            <span className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-gri sm:text-xs">{etiket}</span>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[21px] font-bold tracking-[-.03em] sm:text-[26px]">{deger}</span>
              <span className="text-[11.5px] font-semibold sm:text-[13px]" style={{ color: renk }}>{alt}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-[18px]">
        {/* Izgara */}
        <div className="min-w-0 flex-1 overflow-hidden rounded-[20px] border border-cizgi bg-white shadow-[0_30px_60px_-40px_rgba(11,21,48,.25)] [animation:rise_.6s_.1s_cubic-bezier(.2,.7,.2,1)_both]">
          {/*
            Izgara YATAY KAYAR. Oda sutunu `sticky left-0` ile sabit kalir:
            telefonda 14 gunu kaydirirken hangi odaya baktigini kaybetmemek
            icin bu sart.
          */}
          {/*
            Izgara KENDI icinde kayar (yatay + dikey).
            Boylece gun basligi `sticky top-0` ile yerinde kalir: 18 odayi
            dikey kaydirirken hangi tarihe baktigini kaybetmek, telefonda
            planı okunamaz hale getiriyordu.
          */}
          <div
            ref={kaydirmaRef}
            className="overflow-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
            style={{ maxHeight: dar ? '62dvh' : undefined }}
          >
          <div style={{ minWidth: izgaraEnAz }}>
          <div className="sticky top-0 z-30 grid border-b border-cizgi bg-[#fbfcfe]" style={{ gridTemplateColumns: `${etiketGen}px 1fr` }}>
            <div className="sticky left-0 z-30 bg-[#fbfcfe] px-3 py-3 text-[12px] font-bold uppercase tracking-[.06em] text-gri sm:px-4 sm:py-3.5 sm:text-[13px]">Oda</div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${gunSayisi},minmax(0,1fr))` }}>
              {gunler.map((g) => {
                const buGun = g === bugun;
                return (
                  <div key={g} className="relative flex flex-col gap-0.5 border-l border-satir py-2 text-center sm:py-2.5" style={{ background: buGun ? '#edf2ff' : undefined }}>
                    <span className="text-[9.5px] font-semibold uppercase tracking-[.04em] sm:text-[11px] sm:tracking-[.06em]" style={{ color: buGun ? '#1f4fd8' : '#5b6480' }}>
                      {new Date(`${g}T00:00:00`).toLocaleDateString('tr-TR', { weekday: 'short' })}
                    </span>
                    <span className="text-[12.5px] font-bold sm:text-[15px]" style={{ color: buGun ? '#1f4fd8' : '#0b1530' }}>{kisa(g)}</span>
                    {buGun && <span className="absolute inset-x-0 bottom-0 h-[3px] rounded-t bg-marka-600" />}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            ref={izgaraRef}
            onPointerMove={(e) => {
              imlecRef.current = { x: e.clientX, y: e.clientY };
              const s = surukleRef.current;
              if (!s) {
                /*
                  Henuz kavranmadi. Parmak kaydiysa bu bir KAYDIRMA niyetidir,
                  uzun basma sayacini iptal et - yoksa kaydirmanin ortasinda
                  surukleme baslayip rezervasyon parmakla surukleniyordu.
                */
                if (basmaRef.current) {
                  window.clearTimeout(basmaRef.current);
                  basmaRef.current = undefined;
                  setKavradi(null);
                }
                return;
              }
              const dx = e.clientX - s.x0;
              const dy = e.clientY - s.y0;
              if (Math.abs(dx) + Math.abs(dy) > 3) hareketEttiRef.current = true;
              setSuruklenen({ id: s.id, dx, dy });
            }}
            onPointerUp={birak}
            onPointerLeave={birak}
            className="relative"
            style={{ cursor: suruklenen ? 'grabbing' : 'default' }}
          >
            {yukleniyor ? (
              [0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse border-b border-satir bg-white" style={{ height: satirY }} />
              ))
            ) : odalar.length === 0 ? (
              <p className="px-5 py-14 text-center text-sm text-gri">Bu tesiste tanımlı oda yok.</p>
            ) : (
              odalar.map((oda, oi) => (
                <div
                  key={oda.id}
                  className="grid border-b border-satir [animation:rise_.5s_both] transition-colors"
                  style={{
                    gridTemplateColumns: `${etiketGen}px 1fr`,
                    height: satirY,
                    animationDelay: `${oi * 45}ms`,
                    /*
                      Suruklenen barin satirini yukari kaldir. Her satir ayri
                      bir yigin baglami oldugu icin barin kendi `zIndex`i satir
                      disina cikamiyor: asagidaki satirlara suruklenince baska
                      rezervasyonlarin ARKASINDA kalip gorunmez oluyordu.
                    */
                    position: hedef?.rez.roomId === oda.id ? 'relative' : undefined,
                    zIndex: hedef?.rez.roomId === oda.id ? 20 : undefined,
                    // Hedef satir: gorevli hangi odaya birakacagini kenardan da gorsun
                    background: hedef?.oda.id === oda.id ? (hedef.cakisma ? 'rgba(220,38,38,.05)' : 'rgba(16,185,129,.06)') : undefined,
                    boxShadow: hedef?.oda.id === oda.id ? `inset 3px 0 0 ${hedef.cakisma ? '#dc2626' : '#10b981'}` : undefined,
                  }}
                >
                  <div className="sticky left-0 z-10 flex flex-col justify-center gap-[3px] border-r border-satir bg-white px-2 py-2 sm:px-4 sm:py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[17px] font-bold">{oda.no}</span>
                      {oda.temizlikDurumu === 'KIRLI' && (
                        <span className="rounded-full border border-dashed border-[#e0b23a] bg-[#fff5dc] px-2 py-px text-[11px] font-bold text-[#b9840f]">kirli</span>
                      )}
                      {oda.temizlikDurumu === 'TEMIZ' && oda.durum === 'BOS' && (
                        <span className="rounded-full bg-yesil-zemin px-2 py-0.5 text-[11px] font-bold text-yesil">hazır</span>
                      )}
                      {oda.durum === 'ARIZALI' && (
                        <span className="rounded-full bg-satir px-2 py-0.5 text-[11px] font-bold text-gri">arızalı</span>
                      )}
                    </div>
                    {/*
                      Oda tipi telefonda gizlenir: 100px'lik sutunda zaten
                      "Standart Deniz ..." diye kirpiliyor, yani bilgi
                      vermeden yer kapliyordu. Ayrintiya karta dokununca
                      acilan panelden ulasilir.
                    */}
                    <span className="hidden truncate text-[12.5px] text-gri sm:block">{oda.roomTypeAd ?? '—'}</span>
                  </div>

                  <div className="relative grid" style={{ gridTemplateColumns: `repeat(${gunSayisi},minmax(0,1fr))` }}>
                    {gunler.map((g) => {
                      const dolu = rezervasyonlar.some(
                        (r) =>
                          r.roomId === oda.id &&
                          ['OPSIYON', 'ONAYLI', 'CHECKED_IN'].includes(r.durum) &&
                          r.girisTarihi <= g &&
                          r.cikisTarihi > g,
                      );
                      const acilabilir = yazabilir && !dolu && oda.durum !== 'ARIZALI' && oda.durum !== 'BLOKE';
                      return (
                        <div
                          key={g}
                          onClick={acilabilir ? () => bosHucre(oda.id, g) : undefined}
                          title={acilabilir ? `Oda ${oda.no} · ${kisa(g)} — yeni rezervasyon` : undefined}
                          className={
                            'group relative border-l border-satir transition-colors ' +
                            (acilabilir ? 'cursor-pointer hover:bg-mavi-zemin' : '')
                          }
                          style={{ background: g === bugun ? 'rgba(31,79,216,.04)' : haftaSonu(g) ? '#fbfcfe' : undefined }}
                        >
                          {acilabilir && (
                            <span className="pointer-events-none absolute inset-0 grid place-items-center text-marka-600 opacity-0 transition-opacity group-hover:opacity-100">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                            </span>
                          )}
                        </div>
                      );
                    })}

                    {/*
                      Gecerli seritler: rezervasyonun SIGABILECEGI bos araliklar.
                      Yalnizca hedefin +-2 satir yakinindakiler cizilir: hepsi
                      cizilince izgaranin tamami yesile boyaniyor ve hicbir sey
                      one cikmiyordu - kilavuz olmaktan cikip gurultu oluyordu.
                    */}
                    {suruklenen &&
                      hedef &&
                      Math.abs(oi - hedef.odaIndex) <= 2 &&
                      seritler(oda.id, Math.max(1, gunFarki(hedef.rez.girisTarihi, hedef.rez.cikisTarihi))).map((s2) => (
                        <span
                          key={`serit-${oda.id}-${s2.bas}`}
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-y-3 rounded-lg border border-dashed border-emerald-400/60 bg-emerald-400/[.07] [animation:fadein_.18s_both]"
                          style={{
                            left: `calc(${(s2.bas / gunSayisi) * 100}% + 3px)`,
                            width: `calc(${(s2.uzunluk / gunSayisi) * 100}% - 6px)`,
                            zIndex: 2,
                          }}
                        />
                      ))}

                    {/*
                      Hayalet inis onizlemesi: rezervasyonun TAM olarak
                      kaplayacagi hucreler. Cakisma varsa kirmizi olur ve
                      gorevli daha parmagini kaldirmadan gorur.
                    */}
                    {hedef && hedef.oda.id === oda.id && hedef.degisti && (
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-1 rounded-[13px] border-[2.5px] border-dashed [animation:popin_.16s_both]"
                        style={{
                          left: `calc(${(Math.max(gunFarki(baslangic, hedef.giris), 0) / gunSayisi) * 100}% + 1px)`,
                          width: `calc(${(Math.min(gunFarki(baslangic, hedef.cikis), gunSayisi) - Math.max(gunFarki(baslangic, hedef.giris), 0)) / gunSayisi * 100}% - 2px)`,
                          borderColor: hedef.cakisma ? '#dc2626' : '#10b981',
                          background: hedef.cakisma ? 'rgba(220,38,38,.10)' : 'rgba(16,185,129,.10)',
                          zIndex: 3,
                        }}
                      >
                        {/*
                          Ne olacagini SOZLE de soyle. Etiket barin USTUNDE
                          durur; ortada dururken suruklenen barin altinda
                          kalip misafir adiyla ust uste biniyordu.
                        */}
                        <span
                          className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold text-white shadow-md"
                          style={{ background: hedef.cakisma ? '#dc2626' : '#10b981', zIndex: 40 }}
                        >
                          {hedef.cakisma ? 'Bu oda dolu' : `Oda ${oda.no} · ${kisa(hedef.giris)}`}
                        </span>
                      </span>
                    )}

                    {rezervasyonlar
                      .filter((r) => r.roomId === oda.id && r.cikisTarihi > baslangic && r.girisTarihi < bitis)
                      .map((r) => {
                        const bas = gunFarki(baslangic, r.girisTarihi);
                        const gece = gunFarki(r.girisTarihi, r.cikisTarihi);
                        const solIndex = Math.max(bas, 0);
                        const genislikGun = Math.min(bas + gece, gunSayisi) - solIndex;
                        const [zemin, yazi] = DURUM_RENK[r.durum] ?? ['#94a3b8', '#fff', ''];
                        const tasiniyor = suruklenen?.id === r.id;
                        return (
                          <div
                            key={r.id}
                            onPointerDown={(e) => {
                              if (!yazabilir) return;
                              /*
                                Isaretci artik etkin degilse (hizli dokunus,
                                tarayicinin yeniden hedeflemesi) bu cagri
                                NotFoundError firlatir ve TUM isleyiciyi
                                dusurur - surukleme hic baslamaz. Yakalanmali.
                              */
                              try {
                                e.currentTarget.setPointerCapture?.(e.pointerId);
                              } catch {
                                /* yakalama sart degil; surukleme yine calisir */
                              }
                              const genislik = izgaraRef.current?.clientWidth ?? 1200;
                              const olcu = {
                                id: r.id,
                                x0: e.clientX,
                                y0: e.clientY,
                                gunGen: (genislik - etiketGen) / gunSayisi,
                                odaIndex: oi,
                              };
                              imlecRef.current = { x: e.clientX, y: e.clientY };

                              /*
                                Fare: hemen basla. Dokunma: once 350ms bekle -
                                aksi halde plani kaydirmak isteyen her parmak
                                rezervasyonu tasimaya baslardi.
                              */
                              if (e.pointerType === 'touch') {
                                setKavradi(r.id);
                                basmaRef.current = window.setTimeout(() => {
                                  surukleRef.current = olcu;
                                  setSuruklenen({ id: r.id, dx: 0, dy: 0 });
                                  kenarKaydirmasiniBaslat();
                                }, 350);
                                return;
                              }
                              e.preventDefault();
                              surukleRef.current = olcu;
                              setSuruklenen({ id: r.id, dx: 0, dy: 0 });
                              kenarKaydirmasiniBaslat();
                            }}
                            onPointerCancel={() => {
                              if (basmaRef.current) window.clearTimeout(basmaRef.current);
                              setKavradi(null);
                            }}
                            onClick={() => {
                              if (!hareketEttiRef.current) setSecili(secili === r.id ? null : r.id);
                            }}
                            title={`${r.kod} · ${tamAd(r)} · ${gece} gece${r.not ? ` · ${r.not}` : ''}`}
                            className="absolute inset-y-2.5 flex items-center gap-2 overflow-hidden whitespace-nowrap rounded-[10px] px-3 text-[13.5px] font-semibold hover:brightness-[1.06]"
                            style={{
                              left: `calc(${(solIndex / gunSayisi) * 100}% + 4px)`,
                              width: `calc(${(genislikGun / gunSayisi) * 100}% - 8px)`,
                              background: zemin,
                              color: yazi,
                              /*
                                Bar imleci degil HEDEFI takip eder: gorulen yer
                                ile inecegi yer artik ayni. Kisa gecis, hucreden
                                hucreye kayarken hareketi okunur kilar.
                              */
                              transform: tasiniyor
                                ? `translate(${hedef?.snapDx ?? 0}px,${hedef?.snapDy ?? 0}px) scale(1.03) rotate(-.4deg)`
                                : kavradi === r.id
                                  ? 'scale(1.015)'
                                  : undefined,
                              zIndex: tasiniyor ? 30 : 1,
                              boxShadow: tasiniyor
                                ? '0 22px 44px -14px rgba(11,21,48,.55)'
                                : '0 1px 2px rgba(11,21,48,.08)',
                              outline: tasiniyor && hedef?.cakisma
                                ? '2.5px solid #dc2626'
                                : secili === r.id
                                  ? '2px solid #0b1530'
                                  : undefined,
                              outlineOffset: 2,
                              transition: tasiniyor
                                ? 'transform .13s cubic-bezier(.2,.9,.3,1), box-shadow .15s'
                                : 'left .32s cubic-bezier(.2,.9,.3,1), width .32s cubic-bezier(.2,.9,.3,1), transform .32s cubic-bezier(.2,.9,.3,1), box-shadow .2s',
                              cursor: yazabilir ? (tasiniyor ? 'grabbing' : 'grab') : 'pointer',
                              /*
                                Dokunmada `none` yalnizca surukleme baslayinca
                                gerekir; her zaman kapali olsaydi bar uzerinden
                                plani kaydirmak mumkun olmazdi.
                              */
                              touchAction: yazabilir && tasiniyor ? 'none' : 'pan-x pan-y',
                              animation: reddedilen === r.id ? 'shake .42s cubic-bezier(.36,.07,.19,.97) both' : undefined,
                            }}
                          >
                            {r.kbsDurum === 'GONDERILDI' && (
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-90">
                                <circle cx="7" cy="7" r="6" /><path d="M4.5 7l2 2 3-3.5" />
                              </svg>
                            )}
                            {(r.kbsDurum === 'BEKLIYOR' || r.kbsDurum === 'GONDERILIYOR') && (
                              <span className="size-2 shrink-0 rounded-full bg-white [animation:blink_1.2s_infinite]" />
                            )}
                            {(r.kbsDurum === 'HATA' || r.kbsDurum === 'SURESI_DOLMAK_UZERE') && (
                              <span className="size-2 shrink-0 rounded-full bg-[#ff8b8b]" />
                            )}
                            {/* Tek gecelik cubuk bir gun sutunu kadar dardir; orada tam ad
                                "Ayş…" diye kirpilip en ayirt edici parcayi kaybettirir.
                                Iki gece ve uzeri sigdigi icin tam ad yazilir. */}
                            <span className="min-w-0 flex-1 truncate">
                              {genislikGun >= 2 ? tamAd(r) : (r.misafirSoyad ?? r.kod)}
                            </span>
                            {genislikGun >= 3 && <span className="shrink-0 text-xs font-medium opacity-75">{gece} gece</span>}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))
            )}

            {gunler.includes(bugun) && (
              <div
                className="pointer-events-none absolute inset-y-0 w-0.5 bg-marka-600 [animation:pulseline_2s_ease-in-out_infinite]"
                style={{ left: `calc(${etiketGen}px + (100% - ${etiketGen}px) * ${gunFarki(baslangic, bugun) / gunSayisi})` }}
              />
            )}
          </div>
          </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-satir bg-[#fbfcfe] px-4 py-3 text-[13px] text-gri">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#1f4fd8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
            {yazabilir
              ? 'Rezervasyonu sürükleyip başka odaya veya tarihe bırakın. Çakışan odaya bırakılamaz.'
              : 'Rezervasyon taşımak için yetkiniz yok.'}
            <span
              className="ml-auto font-semibold transition-opacity"
              style={{ color: bildirim?.iyi ? '#12a150' : '#e5484d', opacity: bildirim ? 1 : 0 }}
            >
              {bildirim?.metin}
            </span>
          </div>
        </div>

        {/* Detay paneli */}
        {seciliRez && (
          /*
            Detay paneli.

            Masaustunde 320px/24px dolgu ile fazla yer kapliyordu ve
            "Kimlik ekle" ekranin altindan tasiyordu; olculer sikistirildi
            (288px, 16-18px dolgu, daha kucuk tipografi) ve panel kendi
            icinde kayabiliyor.

            Mobilde yan sutun diye bir sey yok: panel ALT SAYFAYA doner.
            Yan panel 390px'de icerigi ezip oda planini gorunmez yapiyordu.
          */
          <aside className="fixed inset-x-0 bottom-0 z-40 flex max-h-[82dvh] flex-col gap-4 overflow-y-auto rounded-t-[22px] bg-lacivert p-5 text-white [animation:rise_.3s_cubic-bezier(.2,.8,.2,1)_both] lg:sticky lg:inset-auto lg:top-[92px] lg:z-auto lg:max-h-[calc(100dvh-112px)] lg:w-72 lg:shrink-0 lg:rounded-[20px] lg:p-[18px] lg:[animation:slidein_.35s_cubic-bezier(.2,.8,.2,1)_both]">
            {/* Mobil alt sayfa tutamagi */}
            <span className="h-1 w-10 shrink-0 self-center rounded-sm bg-white/25 lg:hidden" />
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-mavi-acik">{seciliRez.kod}</span>
                <h2 className="text-xl font-bold tracking-[-.02em] lg:text-[19px]">
                  {tamAd(seciliRez)}
                </h2>
                {seciliRez.not && (
                  <p className="text-[13px] leading-snug text-mavi-acik">{seciliRez.not}</p>
                )}
              </div>
              <button onClick={() => setSecili(null)} className="size-8 rounded-full bg-white/10 text-base hover:bg-white/20" aria-label="Kapat">×</button>
            </div>

            <div className="grid grid-cols-4 gap-2 lg:grid-cols-2">
              {([
                ['Oda', seciliOda?.no ?? '—'],
                ['Gece', String(gunFarki(seciliRez.girisTarihi, seciliRez.cikisTarihi))],
                ['Giriş', uzun(seciliRez.girisTarihi)],
                ['Çıkış', uzun(seciliRez.cikisTarihi)],
              ] as [string, string][]).map(([e, d]) => (
                <div key={e} className="rounded-lg bg-white/[.07] px-2.5 py-2">
                  <div className="text-[10.5px] leading-tight text-[#b6c2e2]">{e}</div>
                  <div className="truncate text-[13.5px] font-semibold">{d}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col text-[13px]">
              <Satir etiket="Durum" deger={DURUM_RENK[seciliRez.durum]?.[2] ?? seciliRez.durum} renk={DURUM_RENK[seciliRez.durum]?.[0] ?? '#b6c2e2'} />
              <Satir
                etiket="KBS bildirimi"
                deger={seciliRez.kbsDurum ? (KBS_METIN[seciliRez.kbsDurum]?.[0] ?? seciliRez.kbsDurum) : 'Henüz yok'}
                renk={seciliRez.kbsDurum ? (KBS_METIN[seciliRez.kbsDurum]?.[1] ?? '#b6c2e2') : '#b6c2e2'}
              />
              <Satir etiket="Kaynak" deger={KAYNAK_ADI[seciliRez.kaynak] ?? seciliRez.kaynak} />
              {seciliRez.faturaKurumsalMi && (
                <Satir etiket="Fatura" deger={seciliRez.faturaUnvan ?? 'Firmaya'} renk="#8fb0ff" />
              )}
              {/* Telefonla alinan rezervasyonda en cok sorulan sey: kapora geldi mi? */}
              <Satir
                etiket="Kapora"
                deger={
                  seciliRez.kaporaKurus > 0
                    ? `${paraFormatla(seciliRez.kaporaKurus, seciliRez.paraBirimi)} alındı`
                    : 'Alınmadı'
                }
                renk={seciliRez.kaporaKurus > 0 ? '#5fd48f' : '#b6c2e2'}
              />
              <Satir etiket="Kişi" deger={`${seciliRez.yetiskin} yetişkin${seciliRez.cocuk ? `, ${seciliRez.cocuk} çocuk` : ''}`} />
              <div className="flex justify-between pt-2">
                <span className="text-[#b6c2e2]">Toplam</span>
                <span className="text-[15px] font-bold">{paraFormatla(seciliRez.toplamKurus, seciliRez.paraBirimi)}</span>
              </div>
            </div>

            <div className="mt-auto flex flex-col gap-2">
              <button
                onClick={anaIslem}
                disabled={!yetkiler.includes('checkin:perform')}
                className="rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-[#3b66e8] disabled:opacity-40"
              >
                {seciliRez.durum === 'CHECKED_IN' ? 'Check-out yap' : 'Check-in + KBS gönder'}
              </button>

              {/* Uzatma en sik ihtiyac: ayri ve tek dokunusla erisilir. */}
              <div className="flex gap-2">
                <button
                  onClick={() => setDuzelt('uzat')}
                  disabled={!yazabilir}
                  className="flex-1 rounded-full border-[1.5px] border-white/30 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-40"
                >
                  Uzat
                </button>
                <button
                  onClick={() => setDuzelt('duzelt')}
                  disabled={!yazabilir}
                  className="flex-1 rounded-full border-[1.5px] border-white/30 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-40"
                >
                  Düzelt
                </button>
              </div>

              {yetkiler.includes('reservation:cancel') && seciliRez.durum !== 'IPTAL' && (
                <button
                  onClick={() => setIptalAcik(true)}
                  className="rounded-full border-[1.5px] border-white/20 py-3 text-[15px] font-semibold text-[#ff9b9b] transition hover:border-[#ff9b9b] hover:bg-white/5"
                >
                  Rezervasyonu iptal et
                </button>
              )}

              <button
                onClick={() => onCheckIn(seciliRez.id)}
                disabled={!yetkiler.includes('identity:ocr')}
                className="rounded-full border-[1.5px] border-white/30 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-40"
              >
                Kimlik ekle
              </button>
            </div>
          </aside>
        )}
      </div>

      {/*
        Tasima onayi.
        Kat hizmetlerindeki onay penceresiyle ayni desen: gorevli ayni ekranda
        ayni sekilde soru gorsun. Esc = vazgec, Enter = tasi.
      */}
      {onayBekleyen && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4 [animation:fadein_.2s_both]"
          onClick={tasimadanVazgec}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tasima-onay-baslik"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') tasimadanVazgec();
              if (e.key === 'Enter') void tasimayiOnayla();
            }}
            className="w-full max-w-[420px] rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
          >
            <span className="text-[13px] font-semibold text-gri">{onayBekleyen.rez.kod}</span>
            <h2 id="tasima-onay-baslik" className="mt-1 text-[20px] font-bold leading-snug tracking-[-.01em] text-lacivert">
              {tamAd(onayBekleyen.rez)} taşınsın mı?
            </h2>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-zemin px-3 py-1.5 text-[12px] font-bold text-gri">
                Oda {onayBekleyen.eskiOdaNo} · {kisa(onayBekleyen.rez.girisTarihi)}
              </span>
              <svg width="20" height="12" viewBox="0 0 20 12" fill="none" stroke="#7d879c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 6h16m-4-4 4 4-4 4" />
              </svg>
              <span className="rounded-full bg-marka-600 px-3 py-1.5 text-[12px] font-bold text-white">
                Oda {onayBekleyen.yeniOda.no} · {kisa(onayBekleyen.yeniGiris)}
              </span>
            </div>

            {/* Tarih degistiyse ayrica soyle: oda ayni kalip tarih kaymis olabilir */}
            {onayBekleyen.yeniGiris !== onayBekleyen.rez.girisTarihi && (
              <p className="mt-4 rounded-xl bg-zemin px-3.5 py-3 text-[13px] text-gri">
                Tarihler{' '}
                <span className="font-semibold text-lacivert">
                  {kisa(onayBekleyen.rez.girisTarihi)} – {kisa(onayBekleyen.rez.cikisTarihi)}
                </span>{' '}
                yerine{' '}
                <span className="font-semibold text-lacivert">
                  {kisa(onayBekleyen.yeniGiris)} – {kisa(onayBekleyen.yeniCikis)}
                </span>{' '}
                olacak.
              </p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={tasimadanVazgec}
                className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
              >
                Vazgeç
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => void tasimayiOnayla()}
                className="flex-1 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700"
              >
                Taşı
              </button>
            </div>
          </div>
        </div>
      )}

      {iptalAcik && seciliRez && (
        <RezervasyonIptal
          rez={{
            id: seciliRez.id,
            kod: seciliRez.kod,
            ad: tamAd(seciliRez),
            kaporaKurus: seciliRez.kaporaKurus,
            paraBirimi: seciliRez.paraBirimi,
          }}
          onKapat={() => setIptalAcik(false)}
          onIptalEdildi={async (mesaj) => {
            setIptalAcik(false);
            setSecili(null);
            duyur(mesaj, true);
            await yukle();
          }}
        />
      )}

      {duzelt && seciliRez && (
        <RezervasyonDuzelt
          rez={{
            id: seciliRez.id,
            kod: seciliRez.kod,
            roomId: seciliRez.roomId,
            girisTarihi: seciliRez.girisTarihi,
            cikisTarihi: seciliRez.cikisTarihi,
            durum: seciliRez.durum,
            kaynak: seciliRez.kaynak,
            yetiskin: seciliRez.yetiskin,
            cocuk: seciliRez.cocuk,
            toplamKurus: seciliRez.toplamKurus,
            paraBirimi: seciliRez.paraBirimi,
            misafirAd: seciliRez.misafirAd,
            misafirSoyad: seciliRez.misafirSoyad,
            not: seciliRez.not,
          }}
          odalar={odalar.map((o) => ({ id: o.id, no: o.no, roomTypeAd: o.roomTypeAd, durum: o.durum }))}
          mod={duzelt}
          cakisiyorMu={(roomId, g, ck, haricId) => cakisiyorMu({ id: haricId, roomId, giris: g, cikis: ck })}
          onKapat={() => setDuzelt(null)}
          onKaydedildi={async (mesaj) => {
            setDuzelt(null);
            duyur(mesaj, true);
            await yukle();
          }}
        />
      )}

      {yeniRez && (
        <YeniRezervasyon
          odalar={odalar}
          hazir={yeniRez}
          yetkiler={yetkiler}
          cakisiyorMu={(roomId, g, c) => cakisiyorMu({ id: '', roomId, giris: g, cikis: c })}
          onKapat={() => setYeniRez(null)}
          onOlusturuldu={async (rezId, checkInBaslat) => {
            setYeniRez(null);
            duyur('Rezervasyon oluşturuldu', true);
            await yukle();
            if (checkInBaslat) onCheckIn(rezId);
            else setSecili(rezId);
          }}
        />
      )}
    </main>
  );
}

function Satir({ etiket, deger, renk }: { etiket: string; deger: string; renk?: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-white/10 py-[7px]">
      <span className="shrink-0 text-[#b6c2e2]">{etiket}</span>
      <span className="truncate text-right font-semibold" style={{ color: renk }}>{deger}</span>
    </div>
  );
}
